import { GpmPayConfigError } from './errors.js';

/** Mirrors `API_TOKEN_PREFIX` in `apps/backend/src/modules/api-tokens/api-tokens.service.ts`. */
export const API_TOKEN_PREFIX = 'gpm_';

/**
 * `gpm_` (4) + base64url public part (8) + `_` + base64url secret (24) = 37 chars.
 *
 * The width is fixed, so the separating `_` is unambiguous even though base64url
 * itself may contain `_`.
 */
export const API_TOKEN_RE = /^gpm_[A-Za-z0-9_-]{8}_[A-Za-z0-9_-]{24}$/;

export const API_TOKEN_LENGTH = 37;

/** Length of the public prefix stored as `ApiToken.tokenPrefix` in the database. */
const PREFIX_LENGTH = 12;

const CREATE_TOKEN_URL = 'https://app.gpmpay.com/api-tokens';

const MSG_MISSING =
  'apiToken is required — the GPM Pay SDK cannot be used without one.\n' +
  `  1. Create a token at ${CREATE_TOKEN_URL}\n` +
  '  2. Pass it explicitly:  new GpmPay({ apiToken: "gpm_..." })\n' +
  '     or set the GPMPAY_API_TOKEN environment variable and use GpmPay.fromEnv().';

/**
 * Validate an API token locally so a misconfiguration fails immediately with an
 * actionable message instead of a 401 on the first request.
 *
 * The presence check is unconditional. Only the *format* check can be skipped,
 * via `strictTokenFormat: false`, so a future server-side token format cannot
 * brick already-deployed SDK versions.
 *
 * @throws {GpmPayConfigError}
 */
export function assertApiToken(raw: unknown, strict = true): string {
  if (raw === undefined || raw === null) {
    throw new GpmPayConfigError(MSG_MISSING, 'missing_api_token');
  }
  if (typeof raw !== 'string') {
    throw new GpmPayConfigError(
      `apiToken must be a string, received ${typeof raw}.\n\n${MSG_MISSING}`,
      'invalid_api_token',
    );
  }

  const token = raw.trim();
  if (token === '') {
    throw new GpmPayConfigError(MSG_MISSING, 'missing_api_token');
  }
  if (token.startsWith('Bearer ')) {
    throw new GpmPayConfigError(
      'apiToken must not include the "Bearer " prefix — pass the raw gpm_… token; ' +
        'the SDK adds the Authorization header itself.',
      'invalid_api_token',
    );
  }
  if (strict && !API_TOKEN_RE.test(token)) {
    throw new GpmPayConfigError(
      `apiToken has an unexpected format (got "${maskToken(token)}", ${String(token.length)} chars). ` +
        `Expected gpm_XXXXXXXX_… (${String(API_TOKEN_LENGTH)} chars).\n` +
        `Create a fresh token at ${CREATE_TOKEN_URL}. ` +
        'If you are using a newer token format, pass { strictTokenFormat: false }.',
      'invalid_api_token_format',
    );
  }

  return token;
}

/**
 * The token's public prefix — the first 12 chars, matching `ApiToken.tokenPrefix`
 * in the database. Safe to log and to display in support tickets.
 */
export function tokenPrefix(token: string): string {
  return token.slice(0, PREFIX_LENGTH);
}

/** Redacted form for logs and error messages. Never reveals the secret part. */
export function maskToken(token: string): string {
  if (token.length <= PREFIX_LENGTH) return 'gpm_***';
  return `${token.slice(0, PREFIX_LENGTH)}${'•'.repeat(8)}`;
}
